import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { OwlLoader } from "@/components/OwlLoader";

const MIN = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [account, setAccount] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); setError("That link is missing its code."); return; }
    // Take the one-time code out of the address bar so it can't leak through a Referer
    // header, browser history or a screenshot once we've read it.
    window.history.replaceState(null, "", "/reset-password");
    api.get(`/auth/reset-password/${token}`)
      .then(({ data }) => { setAccount(data); setState("ready"); })
      .catch((e) => {
        setError(e.response?.data?.detail || "That reset link is no longer valid.");
        setState("invalid");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Those two passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success("Password changed", {
        description: data.signed_out_devices
          ? `Signed out of ${data.signed_out_devices} device${data.signed_out_devices > 1 ? "s" : ""}. Please sign in again.`
          : "Please sign in with your new password.",
      });
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.detail || "We couldn't change your password. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/40">
      <Card className="w-full max-w-md p-7 shadow-premium" data-testid="reset-password-card">
        {state === "checking" && (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <OwlLoader size="sm" label="Checking your link…" />
          </div>
        )}

        {state === "invalid" && (
          <div className="text-center">
            <span className="icon-chip bg-amber-500/15 text-amber-700 mx-auto mb-4">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <h1 className="text-xl font-semibold mb-2">This link has expired</h1>
            <p className="text-sm text-muted-foreground" data-testid="reset-password-invalid">{error}</p>
            <Link to="/forgot-password">
              <Button className="mt-6 w-full" data-testid="reset-request-new">Request a new link</Button>
            </Link>
          </div>
        )}

        {state === "ready" && (
          <>
            <span className="icon-chip bg-emerald-500/12 text-emerald-700 mb-4">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <h1 className="text-xl font-semibold">Set a new password</h1>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              For <span className="font-medium text-foreground">{account?.email}</span>. Once it's
              saved, every signed-in device is signed out.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="rp-password">New password</Label>
                <Input id="rp-password" type="password" required minLength={MIN} value={password}
                  autoComplete="new-password" onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5" data-testid="reset-password-input" />
                <p className="mt-1.5 text-xs text-muted-foreground">At least {MIN} characters.</p>
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm password</Label>
                <Input id="rp-confirm" type="password" required minLength={MIN} value={confirm}
                  autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5" data-testid="reset-password-confirm" />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="reset-password-error">{error}</p>}
              <Button type="submit" className="w-full"
                disabled={busy || password.length < MIN || !confirm}
                data-testid="reset-password-submit">
                {busy ? "Saving…" : "Save new password"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
